import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import Pagination from "@mui/material/Pagination";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableSortLabel from "@mui/material/TableSortLabel";
import LinearProgress from "@mui/material/LinearProgress";
import { alpha, useTheme } from "@mui/material/styles";

import PublicIcon from "@mui/icons-material/Public";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Close";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import PaymentsIcon from "@mui/icons-material/Payments";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import InfoIcon from "@mui/icons-material/InfoOutlined";

import { GetCountryConversionInsightsCall } from "../redux/api/report.api";

const PAGE_SIZE = 10;
const UNKNOWN_LABEL = "(unknown)";

function formatNumber(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "0";
  return v.toLocaleString();
}

/* "IT" → 🇮🇹. Uses regional-indicator code points; returns "" for
   anything that isn't a 2-letter ISO 3166-1 alpha-2 code (e.g. the
   "(unknown)" bucket). */
function isoToFlag(iso) {
  if (!iso || typeof iso !== "string") return "";
  const code = iso.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return "";
  const base = 0x1f1e6;
  const a = "A".charCodeAt(0);
  return String.fromCodePoint(
    base + code.charCodeAt(0) - a,
    base + code.charCodeAt(1) - a
  );
}

/* Best-effort Intl-based country name from ISO code (en locale). */
let regionDisplay = null;
try {
  if (typeof Intl !== "undefined" && Intl.DisplayNames) {
    regionDisplay = new Intl.DisplayNames(["en"], { type: "region" });
  }
} catch (_) {
  regionDisplay = null;
}
function countryDisplayName(iso) {
  if (!iso) return "";
  const code = String(iso).trim().toUpperCase();
  if (code === UNKNOWN_LABEL.toUpperCase()) return "Unknown / not reported";
  if (!regionDisplay) return code;
  try {
    return regionDisplay.of(code) || code;
  } catch (_) {
    return code;
  }
}

/* Quick-filter chips shown on top of the country table. Each entry maps a
   UI label to a backend `filter` value understood by
   getCountryConversionInsights. */
const COUNTRY_QUICK_FILTERS = [
  { id: "all", label: "All" },
  { id: "purchases", label: "With purchases" },
  { id: "high_conversion", label: "High conversion (≥5%)" },
  { id: "known", label: "Hide unknown" },
];

export default function AdminStatsCountryConversion() {
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(true);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("registrations");
  const [order, setOrder] = useState("desc");
  const [filter, setFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const res = await GetCountryConversionInsightsCall({
        page,
        limit: PAGE_SIZE,
        sort,
        order,
        search,
        filter,
      });
      if (cancelled) return;
      if (res && Array.isArray(res.items)) {
        setAuthorized(true);
        setData(res);
      } else {
        setAuthorized(false);
        setData(null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [page, sort, order, search, filter]);

  const totalPages = useMemo(() => {
    if (!data) return 1;
    return Math.max(1, Number(data.pages) || 1);
  }, [data]);

  const items = data?.items ?? [];

  /* Compute the relative bar denominator from THIS PAGE only — that way
     the leader on the page always anchors at 100% and the comparison
     between visible rows reads at a glance, instead of every bar looking
     tiny because the global #1 is 100x bigger than #10. */
  const pageMaxReg = useMemo(
    () => Math.max(1, ...items.map((r) => Number(r.registrations || 0))),
    [items]
  );

  const handleSort = (column) => {
    if (sort === column) {
      setOrder(order === "desc" ? "asc" : "desc");
    } else {
      setSort(column);
      setOrder("desc");
    }
    setPage(1);
  };

  const handleFilterClick = (id) => {
    setFilter(id);
    setPage(1);
  };

  if (!authorized && !loading) return null;

  return (
    <Box sx={{ mb: 4 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", sm: "flex-end" }}
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
            Top countries — registrations & purchases
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Per-country leaderboard with conversion rate (purchases ÷
            registrations × 100). Default sort is by highest registrations;
            click any column to re-sort.
          </Typography>
        </Box>
        <TextField
          size="small"
          placeholder="Search country code…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: { xs: "100%", sm: 240 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchInput("")}
                  aria-label="Clear search"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Stack>

      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
        <Chip
          icon={<PublicIcon />}
          label={`${formatNumber(data?.totals?.countries || 0)} countries`}
          variant="outlined"
          size="small"
          color="primary"
        />
        <Chip
          icon={<HowToRegIcon />}
          label={`${formatNumber(data?.totals?.registrations || 0)} registrations`}
          variant="outlined"
          size="small"
        />
        <Chip
          icon={<PaymentsIcon />}
          label={`${formatNumber(data?.totals?.purchases || 0)} purchases`}
          variant="outlined"
          size="small"
          color="success"
        />
        <Tooltip
          title={`Overall conversion = ${formatNumber(
            data?.totals?.purchases || 0
          )} purchases ÷ ${formatNumber(
            data?.totals?.registrations || 0
          )} registrations × 100`}
        >
          <Chip
            icon={<TrendingUpIcon />}
            label={`${data?.totals?.conversion_rate ?? 0}% overall`}
            variant="outlined"
            size="small"
            color="secondary"
          />
        </Tooltip>
      </Stack>

      <Stack
        direction="row"
        flexWrap="wrap"
        gap={1}
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          Filter:
        </Typography>
        {COUNTRY_QUICK_FILTERS.map((f) => {
          const active = filter === f.id;
          return (
            <Chip
              key={f.id}
              label={f.label}
              size="small"
              color={active ? "primary" : "default"}
              variant={active ? "filled" : "outlined"}
              onClick={() => handleFilterClick(f.id)}
              sx={{ fontWeight: active ? 600 : 500 }}
            />
          );
        })}
      </Stack>

      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          borderColor: alpha(theme.palette.divider, 0.9),
        }}
      >
        <CardHeader
          title={
            search
              ? `Search results for “${search}”`
              : `Page ${data?.page || 1} / ${totalPages}`
          }
          subheader={
            data
              ? `${formatNumber(data.total)} matches · ${PAGE_SIZE} per page · sorted by ${data.sort} ${
                  data.order === "desc" ? "(high → low)" : "(low → high)"
                }`
              : "Loading…"
          }
          titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }}
          subheaderTypographyProps={{ variant: "caption" }}
          action={
            <Tooltip title="Conversion rate caps at 100%. Rows with 0 registrations show 0%.">
              <IconButton size="small" sx={{ mt: 0.5, mr: 0.5 }}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          }
        />
        <CardContent sx={{ pt: 0 }}>
          {loading ? (
            <Stack spacing={1}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  height={44}
                  sx={{ borderRadius: 1 }}
                />
              ))}
            </Stack>
          ) : items.length === 0 ? (
            <Box sx={{ py: 5, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No countries matched.
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 640 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>#</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <TableSortLabel
                        active={sort === "registrations"}
                        direction={sort === "registrations" ? order : "desc"}
                        onClick={() => handleSort("registrations")}
                      >
                        Registrations
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <TableSortLabel
                        active={sort === "purchases"}
                        direction={sort === "purchases" ? order : "desc"}
                        onClick={() => handleSort("purchases")}
                      >
                        Purchases
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title="purchases ÷ registrations × 100">
                        <TableSortLabel
                          active={sort === "rate"}
                          direction={sort === "rate" ? order : "desc"}
                          onClick={() => handleSort("rate")}
                        >
                          Conversion rate
                        </TableSortLabel>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((row, idx) => {
                    const rank = (data?.page - 1) * PAGE_SIZE + idx + 1;
                    const flag = isoToFlag(row.country);
                    const isUnknown = row.country === UNKNOWN_LABEL;
                    const displayName = isUnknown
                      ? "Unknown / not reported"
                      : countryDisplayName(row.country);
                    const regBar =
                      pageMaxReg > 0
                        ? Math.round((row.registrations / pageMaxReg) * 100)
                        : 0;
                    return (
                      <TableRow
                        key={row.country}
                        hover
                        sx={{ "&:last-child td": { borderBottom: 0 } }}
                      >
                        <TableCell sx={{ width: 40, color: "text.secondary" }}>
                          {rank}
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1.25}
                            alignItems="center"
                          >
                            <Box
                              sx={{
                                fontSize: "1.4rem",
                                lineHeight: 1,
                                width: 24,
                                textAlign: "center",
                                flexShrink: 0,
                                opacity: isUnknown ? 0.4 : 1,
                              }}
                              aria-label={row.country}
                            >
                              {flag || "🌐"}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: 240,
                                }}
                                title={displayName}
                              >
                                {displayName}
                              </Typography>
                              {!isUnknown ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {row.country}
                                </Typography>
                              ) : null}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ whiteSpace: "nowrap", minWidth: 140 }}
                        >
                          <Box sx={{ minWidth: 110 }}>
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              sx={{ fontVariantNumeric: "tabular-nums" }}
                            >
                              {formatNumber(row.registrations)}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={regBar}
                              color="primary"
                              sx={{
                                mt: 0.5,
                                height: 4,
                                borderRadius: 999,
                                bgcolor:
                                  theme.palette.mode === "dark"
                                    ? alpha(theme.palette.common.white, 0.08)
                                    : alpha(theme.palette.common.black, 0.06),
                                "& .MuiLinearProgress-bar": {
                                  borderRadius: 999,
                                },
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            whiteSpace: "nowrap",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {formatNumber(row.purchases)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <Box sx={{ minWidth: 90 }}>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              justifyContent="flex-end"
                              alignItems="baseline"
                            >
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                sx={{
                                  color:
                                    row.conversion_rate >= 5
                                      ? "success.main"
                                      : row.conversion_rate >= 1
                                      ? "text.primary"
                                      : "text.secondary",
                                  fontVariantNumeric: "tabular-nums",
                                }}
                              >
                                {row.conversion_rate}%
                              </Typography>
                            </Stack>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(100, row.conversion_rate)}
                              color={
                                row.conversion_rate >= 5
                                  ? "success"
                                  : row.conversion_rate >= 1
                                  ? "info"
                                  : "warning"
                              }
                              sx={{
                                mt: 0.5,
                                height: 4,
                                borderRadius: 999,
                                bgcolor:
                                  theme.palette.mode === "dark"
                                    ? alpha(theme.palette.common.white, 0.08)
                                    : alpha(theme.palette.common.black, 0.06),
                                "& .MuiLinearProgress-bar": {
                                  borderRadius: 999,
                                },
                              }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!loading && items.length > 0 && totalPages > 1 ? (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
              sx={{ pt: 2 }}
            >
              <Typography variant="caption" color="text.secondary">
                Showing {(data.page - 1) * PAGE_SIZE + 1}–
                {Math.min(data.page * PAGE_SIZE, data.total)} of{" "}
                {formatNumber(data.total)}
              </Typography>
              <Pagination
                count={totalPages}
                page={data.page}
                onChange={(_e, p) => setPage(p)}
                size="small"
                color="primary"
                showFirstButton
                showLastButton
              />
            </Stack>
          ) : null}
        </CardContent>
      </Card>
    </Box>
  );
}
